import React, { useState } from "react";

import { stepCompleted } from "EmployeeApplication/actions";
import { getDependents, getEmployeeApplication, getEmployeeForm, useUpdateEmployeePolicyMutation } from "EmployeeApplication/api";
import LifeVariableAmountSelection from "EmployeeCoverage/Components/LifeVariableAmountSelection";
import { useDispatch, useSelector } from "react-redux";
import formatCurrency from "Utils/formatCurrency";
import { formatNameFL } from "Utils/formatName";

import Alert from "Cobalt/Alert";
import BodyText from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { CardSection } from "Cobalt/Card";
import { HeaderTR, Table, TD, TH, TR } from "Cobalt/TableV2";
import CarrierLogo from "Common/CarrierLogo";
import { Li, Span, Ul } from "Common/HTML";
import Icon from "Common/Icon";
import InfoPopover from "Common/InfoPopover";

import { WholeLifeEnrollOrDeclineButton, WholeLifeIneligibleButton } from "./wholeLife.modals";

import type { WholeLifePlan, PlanCost } from "Utils/types";

const ConfirmEnrollmentButton = ({ planID }) => {
  const { id: employeePolicyID } = useSelector(getEmployeeApplication);
  const dependents = useSelector(getDependents);
  const dispatch = useDispatch();
  const [updateEmployeePolicy] = useUpdateEmployeePolicyMutation();
  const [submitting, setSubmitting] = useState(false);

  const confirmEnrollment = async () => {
    setSubmitting(true);
    const dependentIDs = dependents.map((dependent) => dependent.id);
    const { data } = await updateEmployeePolicy({
      id: employeePolicyID,
      isEnrollingWholeLife: "Yes",
      enrolledWholeLifePlanID: planID,
      dependentsEnrollingWholeLife: dependentIDs.reduce((acc, id) => ({ ...acc, [id]: false }), {}),
    });
    if (data) {
      dispatch(stepCompleted("chooseWholeLifePlan"));
      return;
    }
    setSubmitting(false);
  };

  return (
    <Button onClick={confirmEnrollment} disabled={submitting}>
      Continue
    </Button>
  );
};

interface Props {
  plan: WholeLifePlan;
  planCost: PlanCost;
}

export const WholeLifeSinglePlan = ({ plan, planCost }: Props) => {
  const employeeForm = useSelector(getEmployeeForm);
  const employeePolicy = useSelector(getEmployeeApplication);
  const breakdown = planCost.pricingType === "age" ? planCost.costBreakdowns.age : planCost.costBreakdowns.eeOnly;
  const ineligibleDueToAge = breakdown.message === "over_max_age";

  return (
    <>
      <CardSection>
        {ineligibleDueToAge && (
          <Alert className="ml-[150px] mb-4" color="warning">
            You are not eligible for the offered whole life plan due to age restrictions from the carrier for this plan.
          </Alert>
        )}
        <Table>
          <tbody>
            <TR highlightFirstCell={false} withBorder={false}>
              <TD>
                <CarrierLogo plan={plan} maxSize={3} />
              </TD>
              <TD align="left" width="50%">
                <BodyText mt={4}>{plan.display_name}</BodyText>
              </TD>
            </TR>
          </tbody>
        </Table>
      </CardSection>
      <CardSection>
        <BodyText level={6} mb={4}>
          Premium Overview
        </BodyText>
        {plan.variable_coverage ? (
          <LifeVariableAmountSelection plan={plan} planCost={planCost} coverageAmount={employeePolicy.wholeLifeCoverageAmount} />
        ) : (
          <Table>
            <thead>
              <HeaderTR>
                <TH>Person</TH>
                <TH align="left" width="50%">
                  Your Cost
                  {plan.contribution_strategy !== "Non-Contributory" && (
                    <InfoPopover
                      infoTitle="Payroll Contributions"
                      infoText="This is the amount you pay each month, taken directly out of your paycheck."
                      placement="top"
                    />
                  )}
                </TH>
              </HeaderTR>
            </thead>
            <tbody>
              <TR highlightFirstCell={false} withBorder={false}>
                <TD>{formatNameFL(employeeForm)}</TD>
                <TD align="left">{breakdown.message === "over_max_age" ? "Not Eligible" : formatCurrency(breakdown.employeeCost)}</TD>
              </TR>
              {breakdown.message === "over_max_age" ? (
                <TR highlightFirstCell={false} withBorder={false}>
                  <TD>&nbsp;</TD>
                  <TD align="left">
                    <WholeLifeIneligibleButton plan={plan} planCost={planCost} />
                  </TD>
                </TR>
              ) : (
                <>
                  {plan.contribution_strategy === "Non-Contributory" ? (
                    <>
                      <TR highlightFirstCell={false} withBorder={false}>
                        <TD colSpan={99} align="left">
                          Your employer is covering 100% of your Whole Life Insurance costs. You will be automatically enrolled.
                        </TD>
                      </TR>
                      <TR withBorder={false}>
                        <TD>&nbsp;</TD>
                        <TD align="left">
                          <ConfirmEnrollmentButton planID={plan.id} />
                        </TD>
                      </TR>
                    </>
                  ) : (
                    <TR highlightFirstCell={false} withBorder={false}>
                      <TD>&nbsp;</TD>
                      <TD align="left">
                        <WholeLifeEnrollOrDeclineButton plan={plan} planCost={planCost} />
                      </TD>
                    </TR>
                  )}
                </>
              )}
            </tbody>
          </Table>
        )}
      </CardSection>
      <CardSection>
        <BodyText level={6} mb={4}>
          Plan Overview
        </BodyText>
        <Table>
          <thead>
            <HeaderTR>
              <TH>Feature</TH>
              <TH align="left" width="50%">
                Detail
              </TH>
            </HeaderTR>
          </thead>
          <tbody>
            <TR highlightFirstCell={false} withBorder={false}>
              <TD>Plan Type</TD>
              <TD align="left">Whole Life Insurance</TD>
            </TR>
            {plan.coverage_amount && (
              <TR highlightFirstCell={false} withBorder={false}>
                <TD>Coverage Amount</TD>
                <TD align="left">{plan.coverage_amount_display}</TD>
              </TR>
            )}
            {Array.isArray(plan.benefits) && plan.benefits.length > 0 && (
              <TR highlightFirstCell={false} withBorder={false}>
                <TD verticalAlign="top" py={4}>
                  <Span>Other Benefits</Span>
                </TD>
                <TD align="left" pt={4}>
                  <Ul pl={4}>{plan.benefits?.map((benefit, index) => <Li key={index}>{benefit}</Li>)}</Ul>
                </TD>
              </TR>
            )}
          </tbody>
        </Table>
        {plan.has_sbc && (
          <a href={plan.sbc_url} target="_blank" rel="noreferrer">
            <Icon flavor="solid" icon="download" mr={3} />
            View Plan Summary of Benefits and Coverage
          </a>
        )}
      </CardSection>
    </>
  );
};
